//Libs
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import DatePicker from "react-datepicker";

//Local
import BaseModal from "./BaseModal";
import BaseButtonPrimary from "./BaseButtonPrimary";
import BaseButtonSecondary from "./BaseButtonSecondary";
import BaseInputText from "./BaseInputText";
import BaseInputSelect from "./BaseInputSelect";
import {
  ExistingPurchaseOrder,
  PODeliveryLocation,
  OPODeliveryLocation,
  PODeliveryLocationValues,
} from "../models/purchase-order";
import { ExistingVendor } from "../models/vendor";
import { ExistingCraftRecord } from "../models/craft-record";
import {
  ExistingCustomerLocation,
  getReadableLocationAddress,
} from "../models/customer-location";
import { DbRead, DbWrite } from "../database";
import { useAuthStore } from "../store/firebase-auth";
import { useToastMessageStore } from "../store/toast-messages";
import { useSiteKeyLocationsStore } from "../store/site-key-locations";
import { createToastMessageID } from "../utils";
import { convertJSDateToFSTimestamp } from "../utils/convertJSDateToFSTimestamp";
import { Timestamp } from "firebase/firestore";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: ExistingPurchaseOrder;
  siteKey: string;
}

interface FormData {
  deliveryLocation: PODeliveryLocationValues;
  deliveryAddressLine1: string;
  deliveryAddressLine2: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZipCode: string;
  deliveryCountry: string;
  expectedDeliveryDate: Date | null;
}

export default function EditDeliveryInfoDialog(props: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [vendor, setVendor] = useState<ExistingVendor | null>(null);
  const [craftRecord, setCraftRecord] = useState<ExistingCraftRecord | null>(
    null,
  );
  const [customerLocation, setCustomerLocation] =
    useState<ExistingCustomerLocation | null>(null);
  const [deliveryLocationLoading, setDeliveryLocationLoading] = useState(false);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const [siteKeyLocationList] = useSiteKeyLocationsStore((state) => [
    state.siteKeyLocationList,
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      deliveryLocation: props.purchaseOrder.deliveryLocation || "",
      deliveryAddressLine1: props.purchaseOrder.deliveryAddressLine1 || "",
      deliveryAddressLine2: props.purchaseOrder.deliveryAddressLine2 || "",
      deliveryCity: props.purchaseOrder.deliveryCity || "",
      deliveryState: props.purchaseOrder.deliveryState || "",
      deliveryZipCode: props.purchaseOrder.deliveryZipCode || "",
      deliveryCountry: props.purchaseOrder.deliveryCountry || "",
      expectedDeliveryDate:
        props.purchaseOrder.expectedDeliveryDate instanceof Timestamp
          ? props.purchaseOrder.expectedDeliveryDate.toDate()
          : props.purchaseOrder.expectedDeliveryDate
            ? new Date(props.purchaseOrder.expectedDeliveryDate)
            : null,
    },
  });

  const watchDeliveryLocation = watch("deliveryLocation");

  // Fetch vendor data
  useEffect(() => {
    async function fetchVendor() {
      if (!props.purchaseOrder.vendorID) return;

      try {
        const vendorData = await DbRead.vendors.get(
          props.siteKey,
          props.purchaseOrder.vendorID,
        );
        setVendor(vendorData);
      } catch (error) {
        console.error("Error fetching vendor:", error);
        // Try to handle the case where vendor might not exist
        setVendor(null);
      }
    }

    fetchVendor();
  }, [props.purchaseOrder.vendorID, props.siteKey]);

  // Auto-populate vendor address when vendor data becomes available and vendor location is selected
  useEffect(() => {
    if (vendor && watchDeliveryLocation === PODeliveryLocation.VENDOR) {
      setValue("deliveryAddressLine1", vendor.addressLine1 || "");
      setValue("deliveryAddressLine2", vendor.addressLine2 || "");
      setValue("deliveryCity", vendor.city || "");
      setValue("deliveryState", vendor.state || "");
      setValue("deliveryZipCode", vendor.zipCode || "");
      setValue("deliveryCountry", vendor.country || "USA");
    }
  }, [vendor, watchDeliveryLocation, setValue]);

  // Fetch craft record data if linked to job
  useEffect(() => {
    async function fetchCraftRecord() {
      if (!props.purchaseOrder.craftRecordID) {
        setCraftRecord(null);
        return;
      }

      try {
        const craftRecordData = await DbRead.parentRecords.get(
          props.siteKey,
          props.purchaseOrder.craftRecordID,
        );
        setCraftRecord(craftRecordData);
      } catch (error) {
        console.error("Error fetching craft record:", error);
        setCraftRecord(null);
      }
    }

    fetchCraftRecord();
  }, [props.purchaseOrder.craftRecordID, props.siteKey]);

  // Fetch customer location if job address is selected
  useEffect(() => {
    async function fetchCustomerLocation() {
      if (
        watchDeliveryLocation !== PODeliveryLocation.JOB_ADDRESS ||
        !craftRecord?.customerLocationID
      ) {
        setCustomerLocation(null);
        return;
      }

      try {
        const customerLocationData = await DbRead.customerLocations.getSingle(
          props.siteKey,
          craftRecord.customerLocationID,
        );
        setCustomerLocation(customerLocationData);

        // Auto-populate delivery address fields with customer location
        if (customerLocationData) {
          setValue(
            "deliveryAddressLine1",
            customerLocationData.addressLine1 || "",
          );
          setValue(
            "deliveryAddressLine2",
            customerLocationData.addressLine2 || "",
          );
          setValue("deliveryCity", customerLocationData.city || "");
          setValue("deliveryState", customerLocationData.state || "");
          setValue("deliveryZipCode", customerLocationData.zipCode || "");
          setValue("deliveryCountry", customerLocationData.county || "USA");
        }
      } catch (error) {
        console.error("Error fetching customer location:", error);
        setCustomerLocation(null);
      }
    }

    fetchCustomerLocation();
  }, [watchDeliveryLocation, craftRecord, setValue]);

  // Handle delivery location change
  const handleDeliveryLocationChange = (location: PODeliveryLocation) => {
    setValue("deliveryLocation", location);
    setDeliveryLocationLoading(true);

    // Clear address fields when changing delivery location
    setValue("deliveryAddressLine1", "");
    setValue("deliveryAddressLine2", "");
    setValue("deliveryCity", "");
    setValue("deliveryState", "");
    setValue("deliveryZipCode", "");
    setValue("deliveryCountry", "USA");

    // Populate address based on delivery location
    if (location === PODeliveryLocation.OFFICE) {
      // Use the purchase order's location
      const locationToUse = siteKeyLocationList.find(
        (loc) => loc.id === props.purchaseOrder.locationID,
      );

      if (locationToUse) {
        setValue("deliveryAddressLine1", locationToUse.addressLine1 || "");
        setValue("deliveryAddressLine2", locationToUse.addressLine2 || "");
        setValue("deliveryCity", locationToUse.city || "");
        setValue("deliveryState", locationToUse.state || "");
        setValue("deliveryZipCode", locationToUse.zipCode || "");
        setValue("deliveryCountry", locationToUse.country || "USA");
      }
    } else if (location === PODeliveryLocation.VENDOR) {
      if (vendor) {
        // Use vendor address
        setValue("deliveryAddressLine1", vendor.addressLine1 || "");
        setValue("deliveryAddressLine2", vendor.addressLine2 || "");
        setValue("deliveryCity", vendor.city || "");
        setValue("deliveryState", vendor.state || "");
        setValue("deliveryZipCode", vendor.zipCode || "");
        setValue("deliveryCountry", vendor.country || "USA");
      } else {
        console.warn(
          "Vendor data not available when trying to populate address",
        );
        addToastMessage({
          id: createToastMessageID(),
          message:
            "Vendor information is still loading. Please wait a moment and try again.",
          dialog: false,
          type: "warning",
        });
      }
    }
    // For JOB_ADDRESS, the address will be populated by the useEffect that fetches customer location
    // For OTHER, leave fields empty for manual entry

    setDeliveryLocationLoading(false);
  };

  const onSubmit = async (data: FormData) => {
    if (!firebaseUser) return;

    setIsSubmitting(true);
    try {
      await DbWrite.purchaseOrders.update({
        id: props.purchaseOrder.id,
        refPath: props.purchaseOrder.refPath,
        deliveryLocation: data.deliveryLocation,
        deliveryAddressLine1: data.deliveryAddressLine1,
        deliveryAddressLine2: data.deliveryAddressLine2 || null,
        deliveryCity: data.deliveryCity,
        deliveryState: data.deliveryState,
        deliveryZipCode: data.deliveryZipCode,
        deliveryCountry: data.deliveryCountry,
        expectedDeliveryDate: data.expectedDeliveryDate
          ? convertJSDateToFSTimestamp(data.expectedDeliveryDate)
          : null,
        lastModifiedBy: firebaseUser.uid,
      });

      addToastMessage({
        id: createToastMessageID(),
        message: "Delivery information updated successfully",
        dialog: false,
        type: "success",
      });

      props.onClose();
    } catch (error) {
      console.error("Error updating delivery info:", error);
      addToastMessage({
        id: createToastMessageID(),
        message: "Error updating delivery information",
        dialog: false,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal
      open={props.isOpen}
      closeModal={props.onClose}
      title={
        <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white">
          <h1 className="inline-flex items-center text-xl font-semibold">
            Edit Delivery Information
          </h1>
          <button type="button" onClick={props.onClose}>
            <XMarkIcon aria-label="close dialog" className="h-6 text-white" />
          </button>
        </div>
      }
      parentDivStyles="inline-block transform overflow-hidden max-w-screen-lg rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-8">
        {/* Expected Delivery Date */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Expected Delivery Date
          </label>
          <Controller
            name="expectedDeliveryDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={(date) => field.onChange(date)}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select expected delivery date"
                className="block w-full rounded border border-black p-4 text-gray-700 outline-none focus:ring focus:ring-primaryLight sm:text-sm"
                isClearable
              />
            )}
          />
        </div>

        {/* Delivery Location */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Delivery Location
          </label>
          <Controller
            name="deliveryLocation"
            control={control}
            rules={{ required: "Delivery location is required" }}
            render={({ field }) => (
              <BaseInputSelect
                admin={true}
                text=""
                inputName="deliveryLocation"
                onBlur={field.onBlur}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value &&
                    Object.values(PODeliveryLocation).includes(
                      value as PODeliveryLocation,
                    )
                  ) {
                    const deliveryLocation = value as PODeliveryLocation;
                    field.onChange(deliveryLocation);
                    handleDeliveryLocationChange(deliveryLocation);
                  } else {
                    field.onChange("");
                  }
                }}
                value={field.value || ""}
                disabled={deliveryLocationLoading}
              >
                <option value="">Select delivery location</option>
                {Object.values(OPODeliveryLocation).map((location) => (
                  <option
                    key={location}
                    value={location}
                    disabled={
                      location === PODeliveryLocation.JOB_ADDRESS &&
                      !props.purchaseOrder.craftRecordID
                    }
                  >
                    {location}
                    {location === PODeliveryLocation.JOB_ADDRESS &&
                      !props.purchaseOrder.craftRecordID &&
                      " (No job linked)"}
                  </option>
                ))}
              </BaseInputSelect>
            )}
          />
          {errors.deliveryLocation && (
            <div className="mt-1 text-sm text-red-500">
              {errors.deliveryLocation.message}
            </div>
          )}
        </div>

        {/* Address Preview - Show for Office, Vendor, and manually entered addresses */}
        {(watchDeliveryLocation === PODeliveryLocation.OFFICE ||
          watchDeliveryLocation === PODeliveryLocation.VENDOR ||
          watchDeliveryLocation === PODeliveryLocation.OTHER) && (
          <div className="rounded-md bg-gray-50 p-3">
            <p className="mb-2 text-sm font-medium text-gray-700">
              Delivery Address:
            </p>
            <div className="text-sm text-gray-600">
              {watch("deliveryAddressLine1") && (
                <p>{watch("deliveryAddressLine1")}</p>
              )}
              {watch("deliveryAddressLine2") && (
                <p>{watch("deliveryAddressLine2")}</p>
              )}
              {(watch("deliveryCity") ||
                watch("deliveryState") ||
                watch("deliveryZipCode")) && (
                <p>
                  {watch("deliveryCity")}
                  {watch("deliveryCity") && ", "}
                  {watch("deliveryState")} {watch("deliveryZipCode")}
                </p>
              )}
              {watch("deliveryCountry") &&
                watch("deliveryCountry") !== "USA" && (
                  <p>{watch("deliveryCountry")}</p>
                )}
              {!watch("deliveryAddressLine1") &&
                watchDeliveryLocation === PODeliveryLocation.OTHER && (
                  <p className="italic text-gray-400">
                    Enter address details below
                  </p>
                )}
            </div>
          </div>
        )}

        {/* Job Address Preview */}
        {watchDeliveryLocation === PODeliveryLocation.JOB_ADDRESS &&
          customerLocation && (
            <div className="rounded-md bg-blue-50 p-3">
              <p className="mb-2 text-sm font-medium text-blue-700">
                Job Address:
              </p>
              <div className="text-sm text-gray-600">
                {customerLocation.locationName && (
                  <p className="font-medium">{customerLocation.locationName}</p>
                )}
                <p>{getReadableLocationAddress(customerLocation)}</p>
                {customerLocation.zipCode && <p>{customerLocation.zipCode}</p>}
              </div>
            </div>
          )}

        {/* Edit Address Button - Hide for Job Address */}
        {watchDeliveryLocation !== PODeliveryLocation.JOB_ADDRESS && (
          <button
            type="button"
            onClick={() => setIsEditingAddress(!isEditingAddress)}
            className="inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            {isEditingAddress ? "Hide Address Fields" : "Edit Address"}
          </button>
        )}

        {/* Address Editing Fields - Show when editing or when "Other" is selected */}
        {(isEditingAddress ||
          watchDeliveryLocation === PODeliveryLocation.OTHER) && (
          <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
            <div>
              <Controller
                name="deliveryAddressLine1"
                control={control}
                rules={{ required: "Address line 1 is required" }}
                render={({ field }) => (
                  <BaseInputText
                    admin={true}
                    text="Address Line 1"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value || ""}
                    inputName="deliveryAddressLine1"
                    required
                  />
                )}
              />
              {errors.deliveryAddressLine1 && (
                <div className="mt-1 text-sm text-red-500">
                  {errors.deliveryAddressLine1.message}
                </div>
              )}
            </div>

            <div>
              <Controller
                name="deliveryAddressLine2"
                control={control}
                render={({ field }) => (
                  <BaseInputText
                    admin={true}
                    text="Address Line 2"
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    value={field.value || ""}
                    inputName="deliveryAddressLine2"
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Controller
                  name="deliveryCity"
                  control={control}
                  rules={{ required: "City is required" }}
                  render={({ field }) => (
                    <BaseInputText
                      admin={true}
                      text="City"
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      value={field.value || ""}
                      inputName="deliveryCity"
                      required
                    />
                  )}
                />
                {errors.deliveryCity && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.deliveryCity.message}
                  </div>
                )}
              </div>

              <div>
                <Controller
                  name="deliveryState"
                  control={control}
                  rules={{ required: "State is required" }}
                  render={({ field }) => (
                    <BaseInputText
                      admin={true}
                      text="State"
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      value={field.value || ""}
                      inputName="deliveryState"
                      required
                    />
                  )}
                />
                {errors.deliveryState && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.deliveryState.message}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Controller
                  name="deliveryZipCode"
                  control={control}
                  rules={{ required: "ZIP code is required" }}
                  render={({ field }) => (
                    <BaseInputText
                      admin={true}
                      text="ZIP Code"
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      value={field.value || ""}
                      inputName="deliveryZipCode"
                      required
                    />
                  )}
                />
                {errors.deliveryZipCode && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.deliveryZipCode.message}
                  </div>
                )}
              </div>

              <div>
                <Controller
                  name="deliveryCountry"
                  control={control}
                  rules={{ required: "Country is required" }}
                  render={({ field }) => (
                    <BaseInputText
                      admin={true}
                      text="Country"
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      value={field.value || ""}
                      inputName="deliveryCountry"
                      required
                    />
                  )}
                />
                {errors.deliveryCountry && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.deliveryCountry.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <BaseButtonSecondary
            type="button"
            onClick={props.onClose}
            className="px-6 py-2"
          >
            Cancel
          </BaseButtonSecondary>
          <BaseButtonPrimary
            type="submit"
            disabled={isSubmitting}
            isBusy={isSubmitting}
            className="px-6 py-2"
          >
            Save Changes
          </BaseButtonPrimary>
        </div>
      </form>
    </BaseModal>
  );
}
